import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../../components/AppIcon';
import { getMetricsForAssetType, getDebugValue, isCashflowMetricId } from './analysisAssetDebug';
import { SCENARIO_COLORS } from './analysisMetrics';

const ENTITY_SCOPE_OPTIONS = [
  { value: 'all', label: 'Tous les détenteurs' },
  { value: 'personnel', label: 'Personnel' },
  { value: 'corporation', label: 'Inc.' },
];

function mergeEntityMaps(entitiesA = [], entitiesB = []) {
  const map = {};
  for (const e of [...entitiesA, ...entitiesB]) {
    if (e?.id) map[e.id] = e;
  }
  return map;
}

function mergeDebugAssetLists(assetsA = [], assetsB = []) {
  const byId = new Map();
  for (const a of assetsA) {
    if (!a?.id) continue;
    byId.set(a.id, { ...a, inScenarioA: true, inScenarioB: false });
  }
  for (const a of assetsB) {
    if (!a?.id) continue;
    if (byId.has(a.id)) {
      byId.set(a.id, { ...byId.get(a.id), ...a, inScenarioA: true, inScenarioB: true });
    } else {
      byId.set(a.id, { ...a, inScenarioA: false, inScenarioB: true });
    }
  }
  return Array.from(byId.values()).sort((x, y) => (x.name || '').localeCompare(y.name || '', 'fr'));
}

function alignProjectionYears(rowsA = [], rowsB = []) {
  const len = Math.max(rowsA.length, rowsB.length);
  return Array.from({ length: len }, (_, i) => ({
    year: rowsA[i]?.year ?? rowsB[i]?.year,
    rowA: rowsA[i],
    rowB: rowsB[i],
  }));
}

const AnalysisDebugTable = ({
  projectionA = [],
  projectionB = [],
  labelA = 'Scénario A',
  labelB = 'Scénario B',
  compareEnabled = false,
  assetsA = [],
  assetsB = [],
  entitiesA = [],
  entitiesB = [],
  formatCurrency,
}) => {
  const rowsA = projectionA || [];
  const rowsB = projectionB || [];
  const hasA = rowsA.length > 0;
  const hasB = rowsB.length > 0;
  const canCompare = compareEnabled && hasA && hasB;

  const [viewMode, setViewMode] = useState(canCompare ? 'compare' : 'A');
  const [entityScope, setEntityScope] = useState('all');

  useEffect(() => {
    if (!canCompare && viewMode === 'compare') {
      setViewMode(hasA ? 'A' : 'B');
    }
  }, [canCompare, viewMode, hasA, hasB]);

  const entityById = useMemo(
    () => mergeEntityMaps(entitiesA, entitiesB),
    [entitiesA, entitiesB],
  );

  const mergedAssets = useMemo(
    () => mergeDebugAssetLists(assetsA, assetsB),
    [assetsA, assetsB],
  );

  const activeAssets = useMemo(() => {
    const list = mergedAssets.filter((a) => a?.id && a?.name);
    if (entityScope === 'all') return list;
    return list.filter((a) => {
      const ent = entityById[a.entityId];
      if (entityScope === 'corporation') return ent?.type === 'corporation';
      return ent?.type !== 'corporation';
    });
  }, [mergedAssets, entityById, entityScope]);

  const [assetId, setAssetId] = useState('');
  const [metricIds, setMetricIds] = useState([]);

  const selectedAsset = activeAssets.find((a) => a.id === assetId) || activeAssets[0];
  const selectedEntity = selectedAsset ? entityById[selectedAsset.entityId] : null;

  const metrics = useMemo(
    () => (selectedAsset ? getMetricsForAssetType(selectedAsset.type) : []),
    [selectedAsset],
  );

  useEffect(() => {
    if (!activeAssets.length) {
      setAssetId('');
      return;
    }
    if (!activeAssets.some((a) => a.id === assetId)) {
      setAssetId(activeAssets[0].id);
    }
  }, [activeAssets, assetId]);

  useEffect(() => {
    if (!metrics.length) {
      setMetricIds([]);
      return;
    }
    setMetricIds((prev) => {
      const valid = prev.filter((id) => metrics.some((m) => m.id === id));
      if (valid.length > 0) return valid;
      return metrics.map((m) => m.id);
    });
  }, [metrics, selectedAsset?.id]);

  const toggleMetric = (id) => {
    setMetricIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const activeMetrics = metrics.filter((m) => metricIds.includes(m.id));
  const alignedYears = useMemo(() => alignProjectionYears(rowsA, rowsB), [rowsA, rowsB]);

  const singleRows = viewMode === 'B' ? rowsB : rowsA;
  const showCompare = viewMode === 'compare' && canCompare;

  if (!hasA && !hasB) {
    return null;
  }

  const formatCashflowCell = (val, metricId) => {
    if (val == null) return '—';
    const display = typeof val === 'number' ? formatCurrency(val) : String(val);
    if (!isCashflowMetricId(metricId) || typeof val !== 'number') {
      return display;
    }
    const color = val < 0
      ? 'text-destructive'
      : val > 0
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-foreground';
    return <span className={color}>{display}</span>;
  };

  const scenarioChipClass = (mode, color) =>
    `text-xs rounded-full px-3 py-1.5 border transition-colors ${
      viewMode === mode
        ? 'text-white border-transparent'
        : 'border-border text-muted-foreground hover:bg-muted/50'
    }`;

  const renderCashflowSummary = () => {
    if (showCompare) {
      return (
        <table className="w-full text-xs" data-testid="debug-cashflow-by-entity-table">
          <thead>
            <tr className="text-muted-foreground border-b border-border bg-muted/30">
              <th className="py-2 px-3 text-left font-medium" rowSpan={2}>Année</th>
              <th
                className="py-1 px-3 text-center font-medium border-l border-border"
                colSpan={3}
                style={{ color: SCENARIO_COLORS.A }}
              >
                {labelA}
              </th>
              <th
                className="py-1 px-3 text-center font-medium border-l border-border"
                colSpan={3}
                style={{ color: SCENARIO_COLORS.B }}
              >
                {labelB}
              </th>
            </tr>
            <tr className="text-muted-foreground border-b border-border bg-muted/20">
              {['Perso', 'Inc.', 'Total', 'Perso', 'Inc.', 'Total'].map((h, i) => (
                <th
                  key={`${h}-${i}`}
                  className={`py-1 px-2 text-right font-medium whitespace-nowrap ${i === 0 || i === 3 ? 'border-l border-border' : ''}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {alignedYears.map(({ year, rowA, rowB }) => {
              const persoA = rowA?.cashflowImpactPersonnel ?? 0;
              const corpA = rowA?.cashflowImpactCorporation ?? 0;
              const persoB = rowB?.cashflowImpactPersonnel ?? 0;
              const corpB = rowB?.cashflowImpactCorporation ?? 0;
              return (
                <tr key={`cf-${year}`} className="hover:bg-muted/20">
                  <td className="py-1.5 px-3 tabular-nums font-medium">{year}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums border-l border-border">
                    {formatCashflowCell(persoA, 'cashflowImpactPersonnel')}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums">
                    {formatCashflowCell(corpA, 'cashflowImpactCorporation')}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums font-medium">
                    {formatCashflowCell(persoA + corpA, 'cashflowImpact')}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums border-l border-border">
                    {formatCashflowCell(persoB, 'cashflowImpactPersonnel')}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums">
                    {formatCashflowCell(corpB, 'cashflowImpactCorporation')}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums font-medium">
                    {formatCashflowCell(persoB + corpB, 'cashflowImpact')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    const rows = singleRows;
    return (
      <table className="w-full text-xs" data-testid="debug-cashflow-by-entity-table">
        <thead>
          <tr className="text-muted-foreground border-b border-border bg-muted/30">
            <th className="py-2 px-3 text-left font-medium">Année</th>
            <th className="py-2 px-3 text-right font-medium">Cashflow personnel</th>
            <th className="py-2 px-3 text-right font-medium">Cashflow Inc.</th>
            <th className="py-2 px-3 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => {
            const perso = row.cashflowImpactPersonnel ?? 0;
            const corp = row.cashflowImpactCorporation ?? 0;
            const total = perso + corp;
            return (
              <tr key={`cf-${row.year}`} className="hover:bg-muted/20">
                <td className="py-1.5 px-3 tabular-nums font-medium">{row.year}</td>
                <td className="py-1.5 px-3 text-right tabular-nums">
                  {formatCashflowCell(perso, 'cashflowImpactPersonnel')}
                </td>
                <td className="py-1.5 px-3 text-right tabular-nums">
                  {formatCashflowCell(corp, 'cashflowImpactCorporation')}
                </td>
                <td className="py-1.5 px-3 text-right tabular-nums font-medium">
                  {formatCashflowCell(total, 'cashflowImpact')}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderAssetMetricsTable = () => {
    if (!selectedAsset || activeMetrics.length === 0) return null;

    if (showCompare) {
      return (
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card z-10 shadow-sm">
            <tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 px-3 font-medium" rowSpan={2}>Année</th>
              {activeMetrics.map((m) => (
                <th
                  key={m.id}
                  className="py-1 px-3 text-center font-medium border-l border-border"
                  colSpan={2}
                >
                  {m.label}
                </th>
              ))}
            </tr>
            <tr className="text-left text-muted-foreground border-b border-border text-xs">
              {activeMetrics.map((m) => (
                <React.Fragment key={`sub-${m.id}`}>
                  <th
                    className="py-1 px-2 text-right font-medium border-l border-border"
                    style={{ color: SCENARIO_COLORS.A }}
                  >
                    A
                  </th>
                  <th className="py-1 px-2 text-right font-medium" style={{ color: SCENARIO_COLORS.B }}>
                    B
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {alignedYears.map(({ year, rowA, rowB }) => {
              const snapA = rowA?.assetDebugSnapshot?.[selectedAsset.id];
              const snapB = rowB?.assetDebugSnapshot?.[selectedAsset.id];
              return (
                <tr key={year} className="hover:bg-muted/30" data-testid={`debug-row-${year}`}>
                  <td className="py-2 px-3 font-medium tabular-nums">{year}</td>
                  {activeMetrics.map((m) => (
                    <React.Fragment key={m.id}>
                      <td className="py-2 px-2 text-right tabular-nums border-l border-border">
                        {formatCashflowCell(getDebugValue(snapA, m.id), m.id)}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">
                        {formatCashflowCell(getDebugValue(snapB, m.id), m.id)}
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    const rows = singleRows;
    const snapKey = selectedAsset.id;
    return (
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-card z-10 shadow-sm">
          <tr className="text-left text-muted-foreground border-b border-border">
            <th className="py-2 px-3 font-medium">Année</th>
            {activeMetrics.map((m) => (
              <th key={m.id} className="py-2 px-3 font-medium text-right whitespace-nowrap">
                {m.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => {
            const snap = row.assetDebugSnapshot?.[snapKey];
            return (
              <tr key={row.year} className="hover:bg-muted/30" data-testid={`debug-row-${row.year}`}>
                <td className="py-2 px-3 font-medium tabular-nums">{row.year}</td>
                {activeMetrics.map((m) => (
                  <td key={m.id} className="py-2 px-3 text-right tabular-nums text-foreground">
                    {formatCashflowCell(getDebugValue(snap, m.id), m.id)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const viewLabel = viewMode === 'A' ? labelA : viewMode === 'B' ? labelB : `${labelA} vs ${labelB}`;

  return (
    <div className="space-y-0" data-testid="analysis-debug-table">
      <div className="p-4 border-b border-border bg-muted/20">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Table" size={18} className="text-primary" />
          <div>
            <h3 className="text-sm font-semibold text-foreground">Débogage année par année</h3>
            <p className="text-xs text-muted-foreground">
              {viewLabel} — total = somme des impacts par actif (aligné sur les graphiques)
            </p>
          </div>
        </div>

        {canCompare && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="debug-scenario-view">
            <button
              type="button"
              onClick={() => setViewMode('A')}
              className={scenarioChipClass('A')}
              style={viewMode === 'A' ? { backgroundColor: SCENARIO_COLORS.A } : undefined}
            >
              {labelA}
            </button>
            <button
              type="button"
              onClick={() => setViewMode('B')}
              className={scenarioChipClass('B')}
              style={viewMode === 'B' ? { backgroundColor: SCENARIO_COLORS.B } : undefined}
            >
              {labelB}
            </button>
            <button
              type="button"
              onClick={() => setViewMode('compare')}
              className={scenarioChipClass('compare')}
              style={viewMode === 'compare' ? { background: 'linear-gradient(90deg, #3b82f6, #f59e0b)' } : undefined}
            >
              Comparer A / B
            </button>
          </div>
        )}

        {!canCompare && hasA && hasB && (
          <p className="text-xs text-muted-foreground mb-3">
            Scénarios A et B identiques — affichage du scénario A.
          </p>
        )}

        <div className="flex flex-wrap gap-2 mb-4" data-testid="debug-entity-scope">
          {ENTITY_SCOPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setEntityScope(opt.value)}
              className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                entityScope === opt.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:bg-muted/50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="mb-4 overflow-x-auto rounded-lg border border-border/80">
          {renderCashflowSummary()}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Actif</label>
            <select
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="debug-asset-select"
            >
              {activeAssets.length === 0 ? (
                <option value="">Aucun actif</option>
              ) : (
                activeAssets.map((a) => {
                  const ent = entityById[a.entityId];
                  const holder = ent?.type === 'corporation' ? 'Inc.' : 'Perso';
                  const sc = [];
                  if (a.inScenarioA) sc.push('A');
                  if (a.inScenarioB) sc.push('B');
                  return (
                    <option key={a.id} value={a.id}>
                      [{sc.join('+')}] [{holder}] {a.name}
                      ({a.type === 'assurance_participative' ? 'Assurance' : a.type || 'actif'})
                      {a.fromPlanningOnly ? ' — planification' : ''}
                    </option>
                  );
                })
              )}
            </select>
            {selectedEntity && (
              <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5">
                <Icon
                  name={selectedEntity.type === 'corporation' ? 'Building2' : 'User'}
                  size={14}
                />
                <span>
                  {selectedEntity.type === 'corporation' ? 'Inc.' : 'Personnel'}
                  {' — '}
                  {selectedEntity.name}
                </span>
                {showCompare && selectedAsset && (
                  <span className="text-muted-foreground">
                    {' '}
                    (
                    {!selectedAsset.inScenarioA && 'absent A'}
                    {selectedAsset.inScenarioA && selectedAsset.inScenarioB && 'A + B'}
                    {selectedAsset.inScenarioA && !selectedAsset.inScenarioB && 'A seulement'}
                    {!selectedAsset.inScenarioA && selectedAsset.inScenarioB && 'B seulement'}
                    )
                  </span>
                )}
              </p>
            )}
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-1">Indicateurs</p>
            <div className="flex flex-wrap gap-2" data-testid="debug-metric-toggles">
              {metrics.map((m) => (
                <label
                  key={m.id}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full border border-border px-2.5 py-1 cursor-pointer hover:bg-muted/50"
                >
                  <input
                    type="checkbox"
                    checked={metricIds.includes(m.id)}
                    onChange={() => toggleMetric(m.id)}
                    className="rounded border-input"
                  />
                  {m.label}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {!selectedAsset || activeMetrics.length === 0 ? (
        <p className="text-sm text-muted-foreground p-6 text-center">
          Sélectionnez un actif et au moins un indicateur.
        </p>
      ) : (
        <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
          {renderAssetMetricsTable()}
        </div>
      )}
    </div>
  );
};

export default AnalysisDebugTable;